import { cn } from "@peerigon/pupper/tailwind";
import { cva } from "class-variance-authority";
import React from "react";
import { DatoAsset, DatoAssetData } from "src/components/Dato/DatoAsset";
import {
	StructuredText,
	StructuredTextData,
} from "src/components/StructuredText/StructuredText";
import { readFromEnumFactory } from "src/lib/utils";

export type ImageTextSectionProps = {
	imageData: DatoAssetData;
	structuredText?: StructuredTextData;
	color: Color;
	variant: Variant;
};

export const colors = ["blue", "gray", "blue-gradient"] as const;

type Color = (typeof colors)[number];

export const readColor = readFromEnumFactory(colors, "blue");

export const variants = ["image-left", "image-right", "image-cover"] as const;

type Variant = (typeof variants)[number];

export const readVariant = readFromEnumFactory(variants, "image-cover");

const colorClassNames = {
	blue: "bg-brand-blue text-white",
	gray: "bg-brand-light-bright text-brand-blue",
	"blue-gradient":
		"bg-[radial-gradient(50%_600%_at_50%_50%,var(--chakra-colors-brand-blue)_31.33%,var(--chakra-colors-brand-blue-dark)_100%)] text-white",
} satisfies Record<Color, string>;

const sectionStyles = {
	"image-cover":
		"border-b-0 md:border-b-[1rem] border-b-(--highlight-color)",
	"image-left": "grid grid-cols-[1fr_min(1280px,100%)_1fr] py-12 px-4",
	"image-right": "grid grid-cols-[1fr_min(1280px,100%)_1fr] py-12 px-4",
} satisfies Record<Variant, string>;

const gridStyles = cva("grid grid-cols-1 h-full", {
	variants: {
		variant: {
			"image-cover":
				"md:grid-cols-[1fr_min(1280px,100%)_1fr] md:relative",
			"image-left":
				"md:grid-cols-[2fr_3fr] col-start-2 col-span-1 gap-12",
			"image-right":
				"md:grid-cols-[3fr_2fr] col-start-2 col-span-1 gap-12",
		},
	},
});

const textStyles = cva("flex flex-col justify-center", {
	variants: {
		variant: {
			"image-cover":
				"py-8 md:py-16 order-2 px-8 md:px-4 text-center md:text-left [&>div>a]:min-w-80 [&>div>a]:w-full md:[&>div>a]:w-fit [&>div>a]:mt-4",
			"image-left": "order-2 md:order-2",
			"image-right": "order-2 md:order-1",
		},
	},
});

const imageStyles = cva("relative order-1", {
	variants: {
		variant: {
			"image-cover": "md:absolute md:right-0 md:w-1/2 w-full h-full",
			"image-left":
				"md:order-1 flex items-center h-full min-h-80 md:min-h-full",
			"image-right":
				"md:order-2 flex items-center h-full min-h-80 md:min-h-full",
		},
	},
});

export const ImageTextSection: React.FC<ImageTextSectionProps> = ({
	imageData,
	structuredText,
	color,
	variant,
}) => {
	const colorClassName = colorClassNames[color];

	const image = (
		<div className={imageStyles({ variant })}>
			<DatoAsset
				usePlaceholder={true}
				data={imageData}
				datoImageProps={{
					layout: "fill",
					objectFit: "cover",
				}}
			/>
		</div>
	);

	const text = (
		<div className={textStyles({ variant })}>
			{structuredText && <StructuredText data={structuredText} />}
		</div>
	);

	return (
		<div
			className={cn(
				sectionStyles[variant],
				variant !== "image-cover" && colorClassName,
				"h-full w-full",
			)}
		>
			{variant === "image-cover" && (
				<div className={cn(gridStyles({ variant }), colorClassName)}>
					<div className="md:col-start-2 grid grid-cols-1 md:grid-cols-2 auto-rows-fr">
						{text}
						{image}
					</div>
				</div>
			)}

			{variant !== "image-cover" && (
				<div className={gridStyles({ variant })}>
					{text}
					{image}
				</div>
			)}
		</div>
	);
};

// 🔬 e2e: story
